import { type RefObject, useEffect, useRef } from 'react'

const activeLocks = new Set<symbol>()
let lockedScrollY = 0
let previousRootOverflow = ''
let previousRootOverscroll = ''
let previousBodyPosition = ''
let previousBodyTop = ''
let previousBodyRight = ''
let previousBodyLeft = ''
let previousBodyWidth = ''
let previousBodyOverflow = ''
let previousBodyOverscroll = ''

function lockDocument(token: symbol) {
  if (activeLocks.has(token)) return
  activeLocks.add(token)
  if (activeLocks.size > 1) return

  const root = document.documentElement
  const body = document.body
  lockedScrollY = window.scrollY
  previousRootOverflow = root.style.overflow
  previousRootOverscroll = root.style.overscrollBehavior
  previousBodyPosition = body.style.position
  previousBodyTop = body.style.top
  previousBodyRight = body.style.right
  previousBodyLeft = body.style.left
  previousBodyWidth = body.style.width
  previousBodyOverflow = body.style.overflow
  previousBodyOverscroll = body.style.overscrollBehavior

  root.classList.add('overlay-open')
  root.style.overflow = 'hidden'
  root.style.overscrollBehavior = 'none'
  body.style.position = 'fixed'
  body.style.top = `-${lockedScrollY}px`
  body.style.right = '0'
  body.style.left = '0'
  body.style.width = '100%'
  body.style.overflow = 'hidden'
  body.style.overscrollBehavior = 'none'
}

function unlockDocument(token: symbol) {
  if (!activeLocks.delete(token) || activeLocks.size > 0) return

  const root = document.documentElement
  const body = document.body
  root.classList.remove('overlay-open')
  root.style.overflow = previousRootOverflow
  root.style.overscrollBehavior = previousRootOverscroll
  body.style.position = previousBodyPosition
  body.style.top = previousBodyTop
  body.style.right = previousBodyRight
  body.style.left = previousBodyLeft
  body.style.width = previousBodyWidth
  body.style.overflow = previousBodyOverflow
  body.style.overscrollBehavior = previousBodyOverscroll
  window.scrollTo({ top: lockedScrollY, left: 0, behavior: 'instant' })
}

export function useOverlayScrollLock(open: boolean) {
  const tokenRef = useRef(Symbol('overlay-scroll-lock'))

  useEffect(() => {
    const token = tokenRef.current
    if (!open) return
    lockDocument(token)
    return () => unlockDocument(token)
  }, [open])
}

function revealControl(target: HTMLElement) {
  window.requestAnimationFrame(() => {
    const vp = window.visualViewport
    if (vp) {
      // On iOS (Capacitor), visualViewport shrinks when keyboard opens.
      // Scroll the target so it sits 12px above the visible viewport bottom.
      const rect = target.getBoundingClientRect()
      const vpBottom = vp.offsetTop + vp.height
      const gap = 12
      if (rect.bottom > vpBottom - gap) {
        const scrollNeeded = rect.bottom - (vpBottom - gap)
        window.scrollBy({ top: scrollNeeded, behavior: 'smooth' })
      }
    } else {
      target.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' })
    }
  })
}

export function useOverlayFocusReveal(
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  enabled = true,
) {
  useEffect(() => {
    const panel = panelRef.current
    if (!open || !enabled || !panel) return

    const revealFocusedControl = (event: FocusEvent) => {
      const target = event.target
      if (
        !(target instanceof HTMLElement) ||
        !target.matches('input, textarea, select, [contenteditable="true"]')
      ) return

      revealControl(target)
    }

    const revealActiveControl = () => {
      const target = document.activeElement
      if (
        target instanceof HTMLElement &&
        panel.contains(target) &&
        target.matches('input, textarea, select, [contenteditable="true"]')
      ) revealControl(target)
    }

    panel.addEventListener('focusin', revealFocusedControl)
    window.visualViewport?.addEventListener('resize', revealActiveControl)
    return () => {
      panel.removeEventListener('focusin', revealFocusedControl)
      window.visualViewport?.removeEventListener('resize', revealActiveControl)
    }
  }, [enabled, open, panelRef])
}
